import React, { useEffect, useState } from 'react';
import { useDebounce } from 'hooks';
import styled from 'styled-components';
import {
  Button,
  Input,
  Empty,
  Modal,
  Text,
  Icon,
  message,
  Radio,
  RadioGroup,
} from 'components';
import {
  getSubscriptionByKeySerialNumber,
  IGetSubscriptionByKeySerialNumber,
  addManualCoopKey,
} from 'api/SupraController';
import { featureFlagClient } from 'utils';
import { isMultiResponse } from 'api/SupraController/getSubscriptionByKeySerialNumber';
import AgentLabel from './AgentLabel';

type ManualCoopModalProps = {
  visible: boolean;
  setVisible: React.Dispatch<React.SetStateAction<boolean>>;
  manualCoopSerial: string;
  setManualCoopSerial: React.Dispatch<React.SetStateAction<string>>;
  manualCoopPin: string;
  setManualCoopPin: React.Dispatch<React.SetStateAction<string>>;
};

const ManualCoopModal: React.FC<ManualCoopModalProps> = ({
  visible,
  setVisible,
  manualCoopSerial,
  setManualCoopSerial,
  manualCoopPin,
  setManualCoopPin,
}) => {
  const [searchStatus, setSearchStatus] = useState<
    'idle' | 'loading' | 'success' | 'error'
  >('idle');

  const [subscription, setSubscription] =
    useState<IGetSubscriptionByKeySerialNumber | null>(null);
  const [selectedLinkedUserId, setSelectedLinkedUserId] = useState<
    string | null
  >(null);
  const debouncedSerial = useDebounce(manualCoopSerial, 500);
  const debouncedPin = useDebounce(manualCoopPin, 500);
  const isLinkAccountPhase2Enabled = featureFlagClient.featureEnabled(
    'linkAccountPhaseFeature',
    2,
  );

  useEffect(() => {
    const search = async () => {
      if (
        debouncedSerial &&
        /^\d{8}$/.test(debouncedSerial) &&
        debouncedPin &&
        /^\d{4}$/.test(debouncedPin)
      ) {
        setSearchStatus('loading');
        try {
          const results = await getSubscriptionByKeySerialNumber(
            Number(debouncedSerial),
            debouncedPin,
          );

          // if isLinkAccountPhase2Enabled === true then we will get response with multiple users and subscriptions.
          // isMultiResponse type guard to determine if results consist single subscription and user or multiple subscriptions and users
          if (isMultiResponse(results)) {
            const { requestOwnerMappingId, subscriptions } = results;
            if (
              subscriptions.some(
                sub =>
                  sub.supraOwnerMappingId.toString() === requestOwnerMappingId,
              )
            ) {
              message.error(
                'The key is already associated with this organization.',
              );
              setSubscription(null);
              setSearchStatus('error');
              return;
            }

            if (
              subscriptions.some(sub =>
                sub.manualCoopingOwnerIds?.some(
                  manualCoopId =>
                    manualCoopId.toString() === results.requestOwnerMappingId,
                ),
              )
            ) {
              message.error(
                'The key is already associated as a co-op with this organization.',
              );
              setSubscription(null);
              setSearchStatus('error');
              return;
            }

            setSubscription(results);
            setSearchStatus(results ? 'success' : 'error');

            return;
          }

          if (
            results.requestOwnerMappingId ===
            results.subscription.supraOwnerMappingId.toString()
          ) {
            message.error(
              'The key is already associated with this organization.',
            );
            setSubscription(null);
            setSearchStatus('error');
          } else if (
            results.subscription.manualCoopingOwnerIds?.some(
              manualCoopId =>
                manualCoopId.toString() === results.requestOwnerMappingId,
            )
          ) {
            message.error(
              'The key is already associated as a co-op with this organization.',
            );
            setSubscription(null);
            setSearchStatus('error');
          } else {
            setSubscription(results);
            setSearchStatus(results ? 'success' : 'error');
          }
        } catch (error) {
          if (
            error?.response?.status === 404 ||
            (error?.isAxiosError && error?.response?.status === 404)
          ) {
            message.error('Invalid key serial number or pin.');
          } else if (
            error?.response?.status === 400 ||
            (error?.isAxiosError && error?.response?.status === 400)
          ) {
            message.error(
              error?.response?.data?.message ||
                'An error occurred while searching for the agent.',
            );
          }
          setSubscription(null);
          setSearchStatus('error');
        }
      } else {
        setSubscription(null);
        setSearchStatus('idle');
      }
    };

    search();
  }, [debouncedSerial, debouncedPin]);

  const foundAgent = subscription && searchStatus === 'success';
  const getAgentLabel = () => {
    if (!foundAgent) {
      return (
        <Empty
          image="../../../../img/default.png"
          imageStyle={{ height: 200 }}
          description="The agent's information will appear once the key serial number and PIN are verified"
        />
      );
    }

    if (isLinkAccountPhase2Enabled) {
      const users = isMultiResponse(subscription) && subscription.users;
      // if there is only one user, we can display their information without showing the radio group to select linked accounts.
      // If there are multiple users, then we will show the radio group to select which user to link the manual coop key to.
      if (users.length === 1) {
        const { firstName, lastName, avatarFilestackId, org } = users[0];
        return (
          <AgentLabelContainer>
            <AgentLabel
              name={`${firstName} ${lastName || ''}`}
              firstName={firstName}
              lastName={lastName}
              logo={avatarFilestackId}
              orgName={org.name}
            />
          </AgentLabelContainer>
        );
      }
      if (users.length > 1) {
        return (
          <RadioGroup
            onChange={e => setSelectedLinkedUserId(e.target.value)}
            value={selectedLinkedUserId}
          >
            {users.map(
              ({ firstName, lastName, avatarFilestackId, org, id }) => (
                <Radio key={id} value={id}>
                  <AgentListContainer>
                    <AgentLabel
                      name={`${firstName} ${lastName || ''}`}
                      firstName={firstName}
                      lastName={lastName}
                      logo={avatarFilestackId}
                      orgName={org.name}
                    />
                  </AgentListContainer>
                </Radio>
              ),
            )}
          </RadioGroup>
        );
      }
    }

    const { firstName, lastName, avatarFilestackId, org } =
      !isMultiResponse(subscription) && subscription.user;
    return (
      <AgentLabelContainer>
        <AgentLabel
          name={`${firstName} ${lastName || ''}`}
          firstName={firstName}
          lastName={lastName}
          logo={avatarFilestackId}
          orgName={org.name}
        />
      </AgentLabelContainer>
    );
  };

  const loadingIcon = (
    <Icon type="antdLoadingOutlined" color="blueBase" width={14} />
  );
  const closeIcon = <Icon type="antdCloseOutlined" color="danger" width={14} />;
  const checkIcon = (
    <Icon type="antdCheckOutlined" color="success" width={14} />
  );

  const getKeySerialInputSuffix = () => {
    if (searchStatus === 'loading') return loadingIcon;
    if (searchStatus === 'success') return checkIcon;
    if (searchStatus === 'error') return closeIcon;
    return null;
  };

  const handleAddManualCoopKey = async () => {
    const reqPayload: {
      keySerialNumber: number;
      pin: string;
      userId?: string;
    } = {
      keySerialNumber: Number(manualCoopSerial),
      pin: manualCoopPin,
    };
    if (isLinkAccountPhase2Enabled && selectedLinkedUserId) {
      reqPayload.userId = selectedLinkedUserId;
    }

    await addManualCoopKey(reqPayload);
    message.success('Manual Co-op Key added');
    setVisible(false);
    setManualCoopSerial('');
    setManualCoopPin('');
    setSelectedLinkedUserId(null);
  };

  return (
    <StyledModal
      title="Add a Manual Co-op Key"
      open={visible}
      onCancel={() => {
        setVisible(false);
        setManualCoopSerial('');
        setManualCoopPin('');
        setSelectedLinkedUserId(null);
      }}
      footer={[
        <Button
          key="cancel"
          onClick={() => {
            setVisible(false);
            setManualCoopSerial('');
            setManualCoopPin('');
            setSelectedLinkedUserId(null);
          }}
        >
          Cancel
        </Button>,
        <Button
          key="add"
          type="primary"
          disabled={!foundAgent}
          onClick={handleAddManualCoopKey}
        >
          Add
        </Button>,
      ]}
    >
      <StyledTopContainer>
        <StyledText>
          When you add a manual co-op key, the agent will gain access to Supra
          lockboxes within your organization. To find an agent, simply search by
          their key serial number and PIN.
        </StyledText>
      </StyledTopContainer>
      <InputsRow>
        <InputGroup>
          <StyledBoldText>Key Serial Number</StyledBoldText>
          <Input
            placeholder="XXXXXXXXX"
            value={manualCoopSerial}
            onChange={e => {
              const value = e.target.value.replace(/\D/g, '');
              setManualCoopSerial(value);
            }}
            suffix={getKeySerialInputSuffix()}
            maxLength={12}
          />
        </InputGroup>
        <InputGroup>
          <StyledBoldText>PIN</StyledBoldText>
          <Input
            placeholder="XXXX"
            value={manualCoopPin}
            onChange={e => {
              const value = e.target.value.replace(/\D/g, '');
              setManualCoopPin(value);
            }}
            suffix={getKeySerialInputSuffix()}
            maxLength={4}
          />
        </InputGroup>
      </InputsRow>
      <StyledBoldText>Agent</StyledBoldText>
      {getAgentLabel()}
    </StyledModal>
  );
};

export default ManualCoopModal;

const StyledModal = styled(Modal)`
  height: 60vh;
  min-height: 425px;
`;

const StyledTopContainer = styled.div`
  margin-bottom: 8px;
`;

const StyledText = styled(Text)`
  display: block;
  color: rgba(0, 0, 0, 0.65);
`;

const StyledBoldText = styled(Text)`
  display: block;
  margin: 16px 0px;
  font-weight: 600;
`;

const InputsRow = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 16px;
`;

const InputGroup = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const AgentLabelContainer = styled.div`
  height: 250px;
`;

const AgentListContainer = styled.div`
  margin-bottom: 16px;
`;
